export const SITE_URL = "https://englishmansbay.com";
export const SITE_NAME = "Little Englishman's Bay Estate";

const img = (file) => `/images/${file}`;
const TOBAGO = "Placeholder · Tobago stock photograph";
const STOCK = "Placeholder · Illustrative stock photograph";

export const IMAGES = {
  hero: { src: img("hero-bay.webp"), label: null },
  shore: { src: img("shore.webp"), label: TOBAGO },
  aerial: { src: img("aerial.webp"), label: TOBAGO },
  hills: { src: img("tobago-hills.webp"), label: TOBAGO },
  bay: { src: img("tobago-bay.webp"), label: "Placeholder · Trinidad & Tobago stock photograph" },
  rainbow: { src: img("rainbow.webp"), label: "Placeholder · Trinidad & Tobago stock photograph" },
};

export const STATUS = { EXISTING: "Existing", DEV: "In Development", PLAN: "Planning" };

export const CATEGORIES = [
  {
    slug: "accommodations",
    numeral: "I",
    title: "Accommodations",
    tagline: "Ways to stay within the landscape",
    intro:
      "A boutique resort is proposed on the estate's elevated ridge, overlooking Tobago's North Coast. Alongside it, three distinct glamping concepts are being developed, each shaped by a different part of the landscape: the ocean edge, the tropical forest, and the bamboo and natural materials of the surrounding land.",
    image: { src: img("ridge-hero.webp"), label: null },
    cardImage: { src: img("ridge-living-poster.webp"), video: [{ src: img("ridge-living.mp4"), type: "video/mp4" }, { src: img("ridge-living.webm"), type: "video/webm" }] },
  },
  {
    slug: "wellness",
    numeral: "II",
    title: "Wellness",
    tagline: "Restoration within the landscape",
    intro:
      "Wellness retreats are an existing part of the estate, set within its natural surroundings of rainforest and coastline.",
    image: { src: img("wellness.webp"), label: STOCK },
  },
  {
    slug: "nature-exploration",
    numeral: "III",
    title: "Nature & Exploration",
    tagline: "Forest, river and waterfall",
    intro:
      "The estate's tropical vegetation, rivers and waterfalls form its living heart. The Nature Park and the River and Waterfalls Trail are both existing parts of the estate.",
    image: { src: img("bamboo-creek.webp"), label: STOCK },
    cardImage: { src: img("heritage-creek.webp"), video: [{ src: img("heritage-creek.webm"), type: "video/webm" }, { src: img("heritage-creek.mp4"), type: "video/mp4" }] },
  },
  {
    slug: "conservation-agriculture",
    numeral: "IV",
    title: "Conservation & Agriculture",
    tagline: "Cocoa heritage and fresh water",
    intro:
      "Two initiatives look after what the land and its water provide: the preservation and cultivation of the estate's cocoa heritage, and the conservation of its natural freshwater resources.",
    image: { src: img("cocoa.webp"), label: STOCK },
    cardImage: { src: img("cocoa-living-poster.webp"), video: [{ src: img("cocoa-living.mp4"), type: "video/mp4" }, { src: img("cocoa-living.webm"), type: "video/webm" }] },
  },
  {
    slug: "marine-waterfront",
    numeral: "V",
    title: "Marine & Waterfront",
    tagline: "Reaching the estate by sea",
    intro:
      "Two projects shape the estate's relationship with the sea: a proposed waterfront marina, and a passenger ferry connecting Pigeon Point with Little Englishman's Bay, which has been designed and is now being built.",
    image: { src: img("marina-plan.webp"), label: null },
    heroImage: { src: img("marina-hero.webp"), label: null },
    cardImage: { src: img("bay-living-poster.webp"), video: [{ src: img("bay-living.mp4"), type: "video/mp4" }, { src: img("bay-living.webm"), type: "video/webm" }] },
  },
  {
    slug: "culture-commerce",
    numeral: "VI",
    title: "Culture & Commerce",
    tagline: "Artisans, food and estate products",
    intro:
      "A proposed rainforest village market would bring together Tobago's Indigenous Arawak heritage, Caribbean pirate history and the island's rich cocoa traditions, with chocolate, crafts, food and entertainment in one cultural and retail destination.",
    image: { src: img("village-market.webp"), label: null },
    cardImage: { src: img("village-market-v3-poster.webp"), video: [{ src: img("village-market-v3.webm"), type: "video/webm" }, { src: img("village-market-v3.mp4"), type: "video/mp4" }] },
  },
];

export const PROJECTS = [
  {
    slug: "the-ridge-resort",
    category: "accommodations",
    name: "The Ridge Resort",
    status: STATUS.PLAN,
    summary:
      "A proposed boutique resort along the estate's elevated ridge, overlooking Tobago's spectacular North Coast. Designed to blend into the rainforest, it offers privacy, comfort and panoramic views while preserving the surrounding trees and vegetation.",
    image: { src: img("ridge-hero.webp"), label: null },
    plan: {
      src: img("ridge-collage.webp"),
      title: "Concept Renderings",
      alt: "Concept renderings of The Ridge Resort: villas, guestrooms, restaurant and bar, pool and gardens, reception and maintenance facilities",
      caption: "Concept renderings of the proposed resort. Final designs are subject to planning.",
    },
    details: {
      stats: [
        ["40", "Accommodation units"],
        ["22", "Villa units"],
        ["18", "Ridge guestrooms"],
        ["11", "Two-storey villas"],
      ],
      features: [
        {
          title: "11 Two-Storey Villas",
          image: img("ridge-villas.webp"),
          text: "Each villa building contains two independent units, one on each floor, giving 22 villa units in total. Each unit is approximately 900 sq ft, with a bedroom, bathroom, living and dining area, kitchenette and private terrace. The villas are positioned among existing and newly planted trees, generously spaced for privacy and a close connection with nature.",
        },
        {
          title: "18 Ridge Guestrooms",
          image: img("ridge-guestrooms.webp"),
          text: "Two principal buildings at the highest point of the resort accommodate 18 guestrooms of approximately 400 sq ft. Each has a bedroom, bathroom and private balcony designed to capture the elevated views of Tobago's North Coast.",
        },
        {
          title: "Reception & Administration",
          image: img("ridge-reception.webp"),
          text: "The ground floor of the first principal building contains the reception lobby, gift shops, administrative offices, conference and meeting facilities, storage, and guest and staff amenities.",
        },
        {
          title: "Restaurant & Bar",
          image: img("ridge-restaurant.webp"),
          text: "The second principal building has an elegant open-air restaurant and bar on its ground floor. It overlooks the surrounding landscape and connects directly with the swimming pool and tropical gardens, an exceptional setting for dining and relaxation.",
        },
        {
          title: "Swimming Pool & Tropical Gardens",
          image: img("ridge-pool.webp"),
          text: "Beautifully landscaped outdoor areas complement the architecture, providing relaxing spaces surrounded by the estate's natural vegetation.",
        },
        {
          title: "Maintenance & Service Facilities",
          image: img("ridge-maintenance.webp"),
          text: "Dedicated operational facilities support the resort's daily activities.",
        },
      ],
      concept:
        "The architectural concept reflects understated English colonial Caribbean elegance: natural materials, spacious verandas, tropical landscaping and a strong connection between the buildings and their surroundings.",
    },
  },
  {
    slug: "ocean-side-glamping-park",
    category: "accommodations",
    name: "Ocean Side Glamping Park",
    status: STATUS.DEV,
    summary: "An oceanfront glamping concept combining comfortable nature-based accommodation with Tobago's coastal scenery.",
    image: { src: img("luxury-glamping.webp"), label: STOCK },
  },
  {
    slug: "rainforest-glamping-park",
    category: "accommodations",
    name: "Rainforest Glamping Park",
    status: STATUS.DEV,
    summary: "A rainforest glamping experience designed around the estate's tropical forest environment.",
    image: { src: img("rainforest-glamping-luxe.webp"), label: STOCK },
  },
  {
    slug: "bamboo-bay-glamping-site",
    category: "accommodations",
    name: "Bamboo Bay Glamping Site",
    status: STATUS.DEV,
    summary: "A distinct glamping concept inspired by bamboo, natural materials, and the surrounding tropical landscape.",
    image: { src: img("bamboo.webp"), label: STOCK },
  },
  {
    slug: "wellness-retreats",
    category: "wellness",
    name: "Wellness Retreats",
    status: STATUS.EXISTING,
    summary: "Wellness retreats set within the estate's natural surroundings of rainforest and coastline.",
    image: { src: img("wellness.webp"), label: STOCK },
  },
  {
    slug: "nature-park",
    category: "nature-exploration",
    name: "Nature Park",
    status: STATUS.EXISTING,
    summary: "The estate's existing natural park, featuring tropical vegetation, biodiversity, and natural landscapes.",
    image: { src: img("nature-parrots.webp"), label: STOCK },
    heroImage: { src: img("nature-parrots-poster.webp"), video: [{ src: img("nature-parrots.webm"), type: "video/webm" }, { src: img("nature-parrots.mp4"), type: "video/mp4" }] },
  },
  {
    slug: "river-and-waterfalls-trail",
    category: "nature-exploration",
    name: "River and Waterfalls Trail",
    status: STATUS.EXISTING,
    summary: "A nature trail project showcasing the estate's rivers, waterfalls, and rainforest environment.",
    image: { src: img("river-trail.webp"), label: STOCK },
    heroImage: { src: img("river-trail-poster.webp"), video: [{ src: img("river-trail.webm"), type: "video/webm" }, { src: img("river-trail.mp4"), type: "video/mp4" }] },
  },
  {
    slug: "cocoa-conservation-estate",
    category: "conservation-agriculture",
    name: "Cocoa Conservation Estate",
    status: STATUS.EXISTING,
    summary:
      "An agricultural conservation project focused on preserving and cultivating the estate's cocoa heritage, including nursery development and propagation.",
    image: { src: img("cocoa.webp"), label: STOCK },
  },
  {
    slug: "rainforest-spring-water",
    category: "conservation-agriculture",
    name: "Rainforest Spring Water",
    status: STATUS.DEV,
    summary:
      "An initiative centered on the estate's natural freshwater resources and their conservation and potential future development.",
    image: { src: img("spring-water-full.webp"), label: null },
    heroImage: { src: img("spring-water-hero2.webp"), label: null },
    plan: { src: img("spring-water-full.webp"), title: "Rainforest Spring Water Concept", alt: "Little Englishman's Bay Estate Rainforest Spring Water bottle beside a rainforest waterfall overlooking the sea" },
  },
  {
    slug: "port-marina",
    category: "marine-waterfront",
    name: "Port Marina",
    status: STATUS.PLAN,
    summary: "A proposed waterfront development intended to support recreational boating, marine tourism, and related activities.",
    image: { src: img("marina-hero.webp"), label: null },
    plan: { src: img("marina-plan.webp"), title: "Marina Concept Plan", alt: "Illustrated concept plan of the proposed Port Marina at Little Englishman's Bay Estate" },
  },
  {
    slug: "ferry-service",
    category: "marine-waterfront",
    name: "Ferry Service",
    status: STATUS.DEV,
    summary:
      "A passenger ferry service connecting Pigeon Point with Little Englishman's Bay, offering an alternative way of reaching the estate by sea. The ferry has been designed and is now being built.",
    image: { src: img("ferry-boat.webp"), label: null },
    gallery: [
      { src: img("ferry-cabin.webp"), alt: "Passenger cabin with rows of seating and a central aisle", caption: "Passenger cabin" },
      { src: img("ferry-helm.webp"), alt: "Helm station and onboard restroom", caption: "Helm station & onboard restroom" },
    ],
  },
  {
    slug: "native-retail-village-market",
    category: "culture-commerce",
    name: "Native Retail Village Market",
    status: STATUS.PLAN,
    summary:
      "An immersive, rainforest-based cultural and retail destination inspired by Tobago's Indigenous Arawak heritage, Caribbean pirate history, and Tobago's rich cocoa traditions.",
    image: { src: img("village-market-v3-poster.webp"), label: null, video: [{ src: img("village-market-v3.webm"), type: "video/webm" }, { src: img("village-market-v3.mp4"), type: "video/mp4" }] },
    details: {
      eyebrow: "The Village",
      heading: "What the Village Will Offer",
      features: [
        { title: "Chocolate Experience", text: "A signature attraction offering Tobago chocolate tastings, bean-to-bar demonstrations, cocoa education, workshops, and chocolate retail." },
        { title: "Pirate Camp", text: "An immersive pirate-themed attraction featuring Caribbean pirate history, themed merchandise, treasure experiences, and interactive displays." },
        { title: "Food Court & Entertainment", text: "Traditional Tobago cuisine, open-air dining, live music, cultural performances, and communal seating." },
        { title: "Artisan Village", text: "Local crafts, souvenirs, fresh produce, spices, and Indigenous cultural demonstrations." },
      ],
      concept:
        "The development will feature traditional timber-and-thatch architecture, tropical landscaping, and winding pedestrian pathways integrated into the rainforest near Little Englishman's Bay.",
    },
    gallery: [
      { src: img("village-market.webp"), alt: "Concept of the village market with the Tobago Chocolate stall, food court and local crafts beneath thatched roofs", caption: "Chocolate Experience & Village Market" },
      { src: img("village-pirate.webp"), alt: "Concept of the Pirate Camp with treasure chests, cannon, lookout tower and pirate flags", caption: "Pirate Camp" },
    ],
    plan: {
      src: img("village-map.webp"),
      title: "Village Site Plan",
      alt: "Illustrated site plan of the Native Village Market in the rainforest near Little Englishman's Bay beach",
      caption: "Concept site plan. Final designs are subject to planning.",
    },
  },
];

export const STATUS_NOTE = {
  [STATUS.EXISTING]: "This is an existing part of the estate.",
  [STATUS.DEV]: "This project is currently in development. Further details will be shared as the project progresses.",
  [STATUS.PLAN]: "This project is a proposal at the planning stage. Further details will be shared as plans progress.",
};

export const getCategory = (slug) => CATEGORIES.find((c) => c.slug === slug);
export const projectsIn = (slug) => PROJECTS.filter((p) => p.category === slug);
export const projectPath = (p) => `/${p.category}/${p.slug}`;

export const NAV = [
  { label: "Home", to: "/" },
  { label: "The Estate", to: "/estate" },
  ...CATEGORIES.map((c) => ({ label: c.title, to: `/${c.slug}`, slug: c.slug })),
  { label: "Contact", to: "/contact" },
];
